import { datePl, type ReservationDto, type ReservationTransferPreviewDto, type ShortageByItemDto } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Group, Modal, NumberInput, Select, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { EmptyState, SectionCard } from '../../components/ui';
import { qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { useDemandRefresh } from './demand-ui';

/**
 * Rezerwacje produktu w magazynie (docs/14 §2.5): dla kogo, ile, do kiedy. Szczegóły celu tylko dla spraw, do
 * których pracownik ma dostęp — inne jako „inna realizacja”. Zwolnienie i przeniesienie — z przyczyną.
 */
export function ReservationsSection({ warehouseId, itemId, unit, manage }: { warehouseId: string; itemId: string; unit: string; manage: boolean }) {
  const q = useQuery({ queryKey: ['reservations', warehouseId, itemId], queryFn: () => api<ReservationDto[]>(`/reservations?warehouseId=${warehouseId}&itemId=${itemId}`) });
  const [release, setRelease] = useState<ReservationDto | null>(null);
  const [transfer, setTransfer] = useState<ReservationDto | null>(null);
  const [manual, setManual] = useState(false);
  return (
    <SectionCard
      title="Rezerwacje"
      padding="sm"
      actions={
        manage && (
          <Button size="xs" variant="light" onClick={() => setManual(true)}>
            Zarezerwuj ręcznie
          </Button>
        )
      }
    >
      {!q.data?.length ? (
        <EmptyState compact title="Brak rezerwacji" />
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Dla</Table.Th>
              <Table.Th>Termin</Table.Th>
              <Table.Th ta="right">Ilość</Table.Th>
              {manage && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {q.data.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>
                  {r.caseId ? (
                    <Anchor component={Link} to={`/sprawy/${r.caseId}?zakladka=materialy`} size="sm">
                      {r.caseNumber}
                    </Anchor>
                  ) : (
                    <Text size="sm">{r.purpose || 'inna realizacja'}</Text>
                  )}
                  <Group gap={4}>
                    {r.taskNumber && (
                      <Text size="xs" c="dimmed">
                        {r.taskNumber}
                      </Text>
                    )}
                    {r.mode === 'reczna' && (
                      <Badge size="xs" variant="light">
                        ręczna{r.expiresAt ? ` do ${datePl(r.expiresAt)}` : ''}
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>{r.neededBy ? datePl(r.neededBy) : '—'}</Table.Td>
                <Table.Td ta="right">
                  {qtyPl(r.quantity)} {unit}
                  {r.picked > 0 && (
                    <Text size="xs" c="dimmed">
                      skompletowano {qtyPl(r.picked)}
                    </Text>
                  )}
                </Table.Td>
                {manage && (
                  <Table.Td>
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      {
                        <>
                          <Button size="compact-xs" variant="subtle" onClick={() => setTransfer(r)}>
                            Przenieś
                          </Button>
                          <Button size="compact-xs" variant="subtle" color="red" onClick={() => setRelease(r)}>
                            Zwolnij
                          </Button>
                        </>
                      }
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {release && <ReleaseModal r={release} unit={unit} onClose={() => setRelease(null)} />}
      {transfer && <TransferModal r={transfer} unit={unit} onClose={() => setTransfer(null)} />}
      {manual && <ManualModal warehouseId={warehouseId} itemId={itemId} unit={unit} onClose={() => setManual(false)} />}
    </SectionCard>
  );
}

function ReleaseModal({ r, unit, onClose }: { r: ReservationDto; unit: string; onClose: () => void }) {
  const [qty, setQty] = useState(r.quantity - r.picked);
  const [reason, setReason] = useState('');
  const refresh = useDemandRefresh();
  const m = useMutation({
    mutationFn: () => api(`/reservations/${r.id}/release`, { method: 'POST', body: { quantity: qty, reason } }),
    onSuccess: () => (notifyOk('Zwolniono rezerwację'), refresh(), onClose()),
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Zwolnienie rezerwacji">
      <Stack>
        <Text size="sm">
          Zwolniony towar wraca do dostępnego i trafi do kolejki potrzeb przy najbliższym przyjęciu lub przeliczeniu. Realizacja {r.caseNumber ?? r.purpose} straci zabezpieczenie.
        </Text>
        <NumberInput label={`Ilość (${unit})`} min={0.001} max={r.quantity - r.picked} decimalScale={3} decimalSeparator="," value={qty} onChange={(v) => setQty(Number(v) || 0)} />
        <Textarea label="Przyczyna" required autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button color="red" onClick={() => m.mutate()} loading={m.isPending} disabled={reason.trim().length < 3 || qty <= 0}>
            Zwolnij
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function TransferModal({ r, unit, onClose }: { r: ReservationDto; unit: string; onClose: () => void }) {
  const shortages = useQuery({ queryKey: ['shortages', r.warehouseId], queryFn: () => api<ShortageByItemDto[]>(`/demands/shortages?warehouseId=${r.warehouseId}`) });
  const targets = (shortages.data?.find((s) => s.itemId === r.itemId)?.lines ?? []).filter((l) => l.lineId !== r.demandLineId);
  const [to, setTo] = useState<string | null>(null);
  const [qty, setQty] = useState(r.quantity - r.picked);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<ReservationTransferPreviewDto | null>(null);
  const refresh = useDemandRefresh();
  const body = { toDemandLineId: to, quantity: qty, reason: reason || 'podgląd' };
  useEffect(() => {
    setPreview(null);
    if (!to || qty <= 0) return;
    void api<ReservationTransferPreviewDto>(`/reservations/${r.id}/transfer/preview`, { method: 'POST', body })
      .then(setPreview)
      .catch(() => setPreview(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, qty]);
  const m = useMutation({
    mutationFn: () => api(`/reservations/${r.id}/transfer`, { method: 'POST', body: { toDemandLineId: to, quantity: qty, reason } }),
    onSuccess: () => (notifyOk('Przeniesiono rezerwację'), refresh(), onClose()),
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Przeniesienie rezerwacji" size="lg">
      <Stack>
        <Select
          label="Na potrzebę"
          placeholder={targets.length ? 'Wybierz realizację z brakiem' : 'Brak innych realizacji z brakiem tego produktu'}
          data={targets.map((t) => ({ value: t.lineId, label: `${t.caseNumber ?? 'bez sprawy'} — brak ${qtyPl(t.shortage)} ${unit}, ${t.neededBy ? `na ${datePl(t.neededBy)}` : 'bez terminu'}${t.taskNumber ? `, ${t.taskNumber}` : ''}` }))}
          value={to}
          onChange={setTo}
        />
        <NumberInput label={`Ilość (${unit})`} min={0.001} max={r.quantity - r.picked} decimalScale={3} decimalSeparator="," value={qty} onChange={(v) => setQty(Number(v) || 0)} />
        {preview && (
          <Alert color={preview.earlierLoses ? 'red' : 'yellow'} title={preview.earlierLoses ? 'Uwaga: traci realizacja o wcześniejszym terminie' : 'Skutek przeniesienia'}>
            <Text size="sm">
              {preview.from.caseNumber ?? 'Rezerwacja ręczna'}
              {preview.from.taskNumber ? ` (${preview.from.taskNumber})` : ''}
              {preview.from.neededBy ? `, termin ${datePl(preview.from.neededBy)}` : ''}: zostanie bez {qtyPl(preview.quantity)} {unit} — brak po przeniesieniu {qtyPl(preview.from.afterShortage)}.
            </Text>
            <Text size="sm">
              {preview.to.caseNumber ?? '—'}
              {preview.to.neededBy ? `, termin ${datePl(preview.to.neededBy)}` : ''}: zyska {qtyPl(preview.quantity)} (brak teraz {qtyPl(preview.to.shortage)}).
            </Text>
          </Alert>
        )}
        <Textarea label="Przyczyna" required autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!to || !preview?.quantity || reason.trim().length < 3}>
            Przenieś
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ManualModal({ warehouseId, itemId, unit, onClose }: { warehouseId: string; itemId: string; unit: string; onClose: () => void }) {
  const [qty, setQty] = useState(1);
  const [purpose, setPurpose] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const refresh = useDemandRefresh();
  const m = useMutation({
    mutationFn: () => api('/reservations', { method: 'POST', body: { warehouseId, itemId, quantity: qty, purpose, expiresAt: expiresAt || null } }),
    onSuccess: () => (notifyOk('Zarezerwowano'), refresh(), onClose()),
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Ręczna rezerwacja">
      <Stack>
        <Text size="sm">Rezerwacja z towaru dostępnego, bez powiązania z zapotrzebowaniem (np. zapas na pilne serwisy). Rezerwację dla konkretnej potrzeby zrób z listy zapotrzebowań.</Text>
        <NumberInput label={`Ilość (${unit})`} min={0.001} decimalScale={3} decimalSeparator="," value={qty} onChange={(v) => setQty(Number(v) || 0)} />
        <TextInput label="Cel" required value={purpose} onChange={(e) => setPurpose(e.currentTarget.value)} />
        <TextInput label="Ważna do (opcjonalnie)" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={qty <= 0 || purpose.trim().length < 3}>
            Zarezerwuj
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
